import { usePage, router } from "@inertiajs/react";
import { useState } from "react";
import type { Item } from "@/interfaces/item";
import type { SharedProps } from "@/types";
import ticketIcon from "@/assets/icons/ticket.svg";
import ConfirmPurchaseModal from "./ConfirmPurchaseModal";
import Card from "@/components/ui/Card";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Icon from "@/components/ui/Icon";

export default function Item({
  item,
  alreadyPurchased,
  goalId,
  className,
}: {
  item: Item & { stock_left: number };
  alreadyPurchased: boolean;
  goalId?: number;
  className?: string;
}) {
  const { props } = usePage<SharedProps>();
  const [quantity, setQuantity] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);

  const goldenPriced =
    props.user.wizard &&
    item.golden_price != null &&
    item.golden_price < item.price;
  const unitPrice = goldenPriced ? (item.golden_price as number) : item.price;
  const totalCost = unitPrice * quantity;
  const canAfford = props.user.balance >= totalCost;
  const idvVerified = props.user.verification_status === "verified";
  const maxQuantity = Math.max(
    1,
    Math.min(
      Math.floor(props.user.balance / unitPrice),
      item.stock != null ? item.stock_left : Infinity,
    ),
  );
  const canOverspendBuy =
    props.user.can_overspend &&
    item.event_related &&
    !canAfford &&
    !(item.id === 3 && props.user.balance < 20);
  const outOfStock = item.stock != null && item.stock_left <= 0;

  // A single disabled-state message keeps the ladder of purchase guards
  // readable; the enabled path is the only branch that renders a real button.
  const blockedLabel = alreadyPurchased
    ? "Already purchased"
    : outOfStock
      ? "Out of stock"
      : !idvVerified
        ? "Verify to buy"
        : item.black_market && !props.user.wizard
          ? "Golden ticket required"
          : !canAfford && !canOverspendBuy
            ? "Not enough tickets"
            : null;

  return (
    <Card
      className={className}
      media={
        item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className={`bg-surface-plain w-full object-contain ${item.super_featured ? "h-64" : "h-40"}`}
          />
        ) : undefined
      }
    >
      <div className="mb-2 flex flex-wrap gap-2">
        {item.black_market && <Badge tone="info">🔮 Black market</Badge>}
        {item.super_featured ? (
          <Badge tone="brand">★ Super featured</Badge>
        ) : (
          item.featured && <Badge tone="brand">★ Featured</Badge>
        )}
        {item.stock != null && !outOfStock && (
          <Badge tone="neutral">{item.stock_left} left</Badge>
        )}
      </div>

      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-heading text-ink leading-tight font-bold">
          {item.name}
        </h2>
        <div className="flex shrink-0 items-center gap-1.5">
          <Icon src={ticketIcon} label="Tickets" className="h-5 w-5" />
          {goldenPriced && (
            <span className="font-display text-body text-ink-soft line-through">
              {item.price * quantity}
            </span>
          )}
          <span
            className={`font-display text-subheading font-bold ${goldenPriced ? "text-carrot-orange" : "text-ink"}`}
          >
            {totalCost}
          </span>
        </div>
      </div>

      <p className="text-body text-ink-muted mt-2">{item.description}</p>

      {props.user.is_admin && (
        <button
          type="button"
          onClick={() =>
            goalId != null
              ? router.delete(`/admin/goals/${goalId}`, {
                  preserveScroll: true,
                })
              : router.post(
                  "/admin/goals",
                  { item_id: item.id },
                  { preserveScroll: true },
                )
          }
          className="border-line bg-surface-alt text-caption text-ink hover:bg-goldenrod mt-3 cursor-pointer self-start rounded-full border px-3 py-1 font-bold transition-colors"
        >
          {goalId != null ? "★ Remove homepage goal" : "+ Add as homepage goal"}
        </button>
      )}

      <div className="mt-auto pt-4">
        {blockedLabel ? (
          <p className="border-line/30 bg-surface-alt font-display text-body text-ink-soft w-full border-2 px-5 py-2.5 text-center font-bold">
            {blockedLabel}
          </p>
        ) : (
          <>
            {!item.one_per_user && (
              <div className="mb-3 flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  aria-label="Decrease quantity"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </Button>
                <span className="font-display text-body w-10 text-center font-bold">
                  {quantity}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  aria-label="Increase quantity"
                  onClick={() => setQuantity((q) => q + 1)}
                  disabled={quantity >= maxQuantity}
                >
                  +
                </Button>
              </div>
            )}

            <Button className="w-full" onClick={() => setShowConfirm(true)}>
              {canOverspendBuy ? "Buy (will go into debt)" : "Buy"}
              {quantity > 1 ? ` (${quantity})` : ""}
            </Button>

            <ConfirmPurchaseModal
              open={showConfirm}
              item={item}
              quantity={quantity}
              totalCost={totalCost}
              willGoIntoDebt={canOverspendBuy}
              onCancel={() => setShowConfirm(false)}
              onConfirm={(note?: string) => {
                setShowConfirm(false);
                router.post(`/shop/${item.id}/buy`, { quantity, note });
              }}
            />
          </>
        )}
      </div>
    </Card>
  );
}
